import React, { useState, useEffect, useCallback } from 'react';
import './EventAttendees.css';

/**
 * EventAttendees Component
 *
 * Simple tile for confirming attendance at an event.
 * Shows confirmation status and allows users to mark attendance.
 */
const EventAttendees = ({
  eventId,
  isPastEvent = false,
  fbRespondents = 0,
  onAttendanceChange = null,
  apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'
}) => {
  const [attendeeData, setAttendeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showFbTooltip, setShowFbTooltip] = useState(false);

  // Fetch attendee data
  const fetchAttendees = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await fetch(
        `${apiBase}/events/attendees/${eventId}`,
        { headers }
      );
      const data = await response.json();

      if (data.success) {
        setAttendeeData(data.data);
      }
    } catch (error) {
      console.error('Error fetching attendees:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId, apiBase]);

  useEffect(() => {
    fetchAttendees();
  }, [fetchAttendees]);

  // Handle attendance confirmation
  const handleConfirmAttendance = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to confirm your attendance');
      return;
    }

    setIsConfirming(true);
    try {
      const response = await fetch(`${apiBase}/events/attendance/${eventId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ showPublicly: false }),
      });
      const data = await response.json();

      if (data.success) {
        fetchAttendees();
        // Notify parent to refresh GiveKudos
        if (onAttendanceChange) onAttendanceChange();
      } else {
        alert(data.error || 'Failed to confirm attendance');
      }
    } catch (error) {
      console.error('Error confirming attendance:', error);
      alert('Failed to confirm attendance');
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle removing attendance
  const handleRemoveAttendance = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setIsConfirming(true);
    try {
      const response = await fetch(`${apiBase}/events/attendance/${eventId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        fetchAttendees();
        // Notify parent to refresh GiveKudos
        if (onAttendanceChange) onAttendanceChange();
      } else {
        alert(data.error || 'Failed to remove attendance');
      }
    } catch (error) {
      console.error('Error removing attendance:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="event-attendees-section loading">
        <div className="spinner-small"></div>
      </div>
    );
  }

  if (!attendeeData) return null;

  const { confirmedCount, userConfirmed } = attendeeData;

  return (
    <section className="event-attendees-section">
      <div className="attendees-header">
        <i className="bi bi-people-fill"></i>
        <h3>Attendees</h3>
      </div>

      {/* Stats */}
      <div className="attendees-stats">
        <div className="stat-row fb-stat">
          <span className="stat-label">
            FB confirmed
            <span
              className="info-icon-wrapper"
              onMouseEnter={() => setShowFbTooltip(true)}
              onMouseLeave={() => setShowFbTooltip(false)}
            >
              <i className="bi bi-info-circle info-icon"></i>
              {showFbTooltip && (
                <div className="fb-tooltip">
                  This number is provided by the community member who added the event, based on the Facebook event page.
                </div>
              )}
            </span>
          </span>
          <span className="stat-value">{fbRespondents}</span>
        </div>
        <div className="stat-row">
          <span className="stat-label">Confirmed on Move n Muse</span>
          <span className="stat-value highlight">{confirmedCount}</span>
        </div>
      </div>

      {/* Confirmation Action */}
      <div className="attendance-action">
        {userConfirmed ? (
          <button className="btn-attendance-confirmed" disabled>
            <i className="bi bi-check-circle-fill"></i>
            Attendance confirmed
          </button>
        ) : (
          <button
            className="btn-confirm-attendance"
            onClick={handleConfirmAttendance}
            disabled={isConfirming}
          >
            {isConfirming ? (
              <>
                <i className="bi bi-hourglass"></i> Confirming...
              </>
            ) : (
              <>
                <i className="bi bi-check2-circle"></i>
                {isPastEvent ? 'I Attended' : "I'm Going"}
              </>
            )}
          </button>
        )}
      </div>
    </section>
  );
};

export default EventAttendees;
